import React, { useState } from 'react'
import {
  Button,
  Modal,
  BouncingLoader,
  RadioButton
} from 'examkrackers-components'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import LocalStorageService from 'services/LocalStorageService'
import { LOCAL_STORAGE_KEYS } from 'utils/storage'
import { useSelector, useDispatch } from 'react-redux'
import {
  setExamDate,
  setPrioriDays,
  postBuildPreReading
} from 'services/CalendarService'
import PATHS from 'utils/paths'
import { countStudyDaysInRange, radioButtonOptions } from 'utils/calendar'
import { getCurrentCourse } from 'modules/courses/ducks/selectors'
import { DATE_FORMATS, formatDate } from 'utils/date'

import ConfirmationSalty from 'assets/images/Calendar_Reset_Confirmation_Saltys.svg'
import WarningCalendar from 'assets/images/WarningCalendar.svg'
import { propOr } from 'ramda'
import { isNotNilOrEmpty } from 'utils/ramda'
import { STUDY_DAYS_FOR_CALENDAR } from './utils/calendarDaysAmount'
import { showToastRoutine } from 'modules/toast/ducks/actions'
import { SEVERITY } from 'utils/toast'
import { getErrorMessage } from 'utils/errors'

interface ResetCalendarConfirmationModalProps {
  values: any
  disabled: boolean
  isLiveCourse?: boolean
  shouldAddPreReading?: boolean
}
const ResetCalendarConfirmationModal = ({
  values,
  disabled,
  isLiveCourse,
  shouldAddPreReading
}: ResetCalendarConfirmationModalProps) => {
  const [open, setOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedConfirmationOption, setSelectedConfirmationOption] = useState(
    radioButtonOptions.reject.value
  )

  const currentCourse = useSelector(getCurrentCourse)

  const accessibleTo = propOr('', 'accessible_to', currentCourse)
  const formatAccessibleTo = accessibleTo
    ? formatDate(accessibleTo, DATE_FORMATS.yearFirst)
    : ''

  const isCheckboxChecked =
    selectedConfirmationOption === radioButtonOptions.confirm.value
  const handleChangeForConfirmation = (value: string) =>
    setSelectedConfirmationOption(value)

  const { t } = useTranslation()
  const { push } = useHistory()
  const dispatch = useDispatch()

  const showToast = React.useCallback(
    (payload: { key: string; severity: string; options?: any }) => dispatch(showToastRoutine(payload)),
    [dispatch]
  )

  const studyDaysCount = countStudyDaysInRange(
    values.calendar_start_at,
    values.exam_at,
    values.prioriDays
  )

  const handleSaveDate = async () => {
    setIsLoading(true)
    const courseId = LocalStorageService.get(LOCAL_STORAGE_KEYS.studentCourseId)

    try {
      const shouldStartManualCalendar = studyDaysCount < STUDY_DAYS_FOR_CALENDAR

      await setPrioriDays({
        id: courseId,
        prioridays:
          JSON.stringify(values.prioriDays) ===
          JSON.stringify([0, 0, 0, 0, 0, 0, 0])
            ? [6, 0, 1, 4, 2, 3, 7]
            : values.prioriDays
      })
      await setExamDate({
        calendar_start_at: values.calendar_start_at,
        exam_at: values.exam_at,
        course_id: courseId,
        mcat_date_id: values.mcat_at,
        build_calendar: shouldStartManualCalendar ? -1 : 1
      })

      setIsLoading(false)
      handleClose()
      push(PATHS.calendar)
    } catch (handleError) {
      setIsLoading(false)
      // Get status code from error response
      const statusCode = (handleError as any)?.response?.status || 
        (handleError as any)?.response?.data?.statusCode || 
        null
      
      
      const errorMessage = getErrorMessage(handleError as Error) || 
        (handleError as any)?.response?.data?.message || 
        (handleError as any)?.message || 
        ''
      
      if (statusCode === 422 && errorMessage) {
        showToast({
          key: 'toast.custom',
          severity: SEVERITY.error,
          options: {
            content: errorMessage
          }
        })
      } else {
        showToast({
          key: 'toast.somethingWentWrong',
          severity: SEVERITY.error
        })
      }
    }
  }

  const handleSaveDateWithPreReading = async () => {
    setIsLoading(true)
    const courseId = LocalStorageService.get(LOCAL_STORAGE_KEYS.studentCourseId)
    try {
      await setPrioriDays({
        id: courseId,
        prioridays: values.prioriDays
      })

      const preReadingAdditionalPayload = shouldAddPreReading
        ? {
            is_pre_reading: true,
            pre_reading_end_date: isNotNilOrEmpty(values.prereading_end_date)
              ? values.prereading_end_date
              : values.exam_at
          }
        : {}

        await setExamDate({
        calendar_start_at: values.calendar_start_at,
        exam_at: formatAccessibleTo,
        course_id: courseId,
        mcat_date_id: values.mcat_at,
        build_calendar: -3,
        ...preReadingAdditionalPayload
      })

      if (shouldAddPreReading && isNotNilOrEmpty(values.prereading_end_date)) {
        await postBuildPreReading({
          date_end: values.prereading_end_date,
          is_reset: true,
          date_start: values.calendar_start_at
        })
      }

      setIsLoading(false)
      handleClose()
      push(PATHS.calendar)
    } catch (e) {
      setIsLoading(false)
      // Get status code from error response
      const statusCode = (e as any)?.response?.status || 
      (e as any)?.response?.data?.statusCode || 
      null
      
      const errorMessage = getErrorMessage(e as Error) || 
        (e as any)?.response?.data?.message || 
        (e as any)?.message || 
        ''
      
      if (statusCode === 422 && errorMessage) {
        showToast({
          key: 'toast.custom',
          severity: SEVERITY.error,
          options: {
            content: errorMessage
          }
        })
      } else {
        showToast({
          key: 'toast.somethingWentWrong',
          severity: SEVERITY.error
        })
      }
    }
  }

  const handleSelectSubmit = isLiveCourse
    ? handleSaveDateWithPreReading
    : handleSaveDate

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)
  const handleCancel = () => {
    handleClose()
    setSelectedConfirmationOption(radioButtonOptions.reject.value)
  }

  return (
    <>
      <ButtonContainer
        data-testid='ResetCalendarConfirmationModal-ButtonContainer'
        color='secondary'
        disabled={disabled}
        onClick={handleOpen}
      >
        {t('calendar.reset.button')}
      </ButtonContainer>
      <Modal
        data-testid='ResetCalendarConfirmationModal-Modal'
        open={open}
        handleClose={isLoading ? () => {} : handleClose}
        hasBorder
        title={
          isLoading
            ? t('calendar.reset.modal.loading')
            : t('calendar.reset.modal.title')
        }
        titleIcon={
          isLoading ? null : (
            <TitleIconContainer data-testid='ResetCalendarConfirmationModal-TitleIconContainer'>
              <TitleIconWrapper data-testid='ResetCalendarConfirmationModal-TitleIconWrapper'>
                <WarningCalendarWrapper data-testid='ResetCalendarConfirmationModal-WarningCalendarWrapper'>
                  <WarningCalendarIcon
                    data-testid='ResetCalendarConfirmationModal-WarningCalendarIcon'
                    id='warning'
                    src={WarningCalendar}
                    alt='Warning'
                  />
                </WarningCalendarWrapper>
              </TitleIconWrapper>
            </TitleIconContainer>
          )
        }
      >
        {isLoading ? (
          <LoaderContainer data-testid='ResetCalendarConfirmationModal-LoaderContainer'>
            <BouncingLoader data-testid='ResetCalendarConfirmationModal-BouncingLoader' />
          </LoaderContainer>
        ) : (
          <div>
            <MainWrapper data-testid='ResetCalendarConfirmationModal-MainWrapper'>
              <TextWrapper data-testid='ResetCalendarConfirmationModal-TextWrapper-descriptionOne'>
                <DescWrapper data-testid='ResetCalendarConfirmationModal-DescWrapper-descriptionOne'>
                  {t('calendar.reset.modal.descriptionOne')}
                </DescWrapper>
                <DescWrapper data-testid='ResetCalendarConfirmationModal-DescWrapper-descriptionTwo'>
                  {t('calendar.reset.modal.descriptionTwo')}
                </DescWrapper>
                <DescWrapper data-testid='ResetCalendarConfirmationModal-DescWrapper-descriptionThree'>
                  {t('calendar.reset.modal.descriptionThree')}
                </DescWrapper>
                <RadioButtonContainer data-testid='ResetCalendarConfirmationModal-RadioButtonContainer'>
                  <RadioButton
                    data-testid='ResetCalendarConfirmationModal-DescWrapper-RadioButton'
                    alignTop
                    label={t('calendar.reset.modal.confirmation')}
                    name='confirmationYes'
                    value={radioButtonOptions.confirm.value}
                    isChecked={
                      selectedConfirmationOption ===
                      radioButtonOptions.confirm.value
                    }
                    onChange={handleChangeForConfirmation}
                  />
                </RadioButtonContainer>
              </TextWrapper>
              <ConfirmationSaltyImage
                data-testid='ResetCalendarConfirmationModal-ConfirmationSaltyImage'
                id='reorganize-salty'
                src={ConfirmationSalty}
                alt='Salty reorganize'
              />
            </MainWrapper>
            <ButtonsWrapper data-testid='ResetCalendarConfirmationModal-ButtonsWrapper'>
              <ButtonWrapper
                color='tertiary'
                onClick={handleCancel}
                data-testid='ResetCalendarConfirmationModal-ButtonWrapper-cancel'
              >
                {t('calendar.button.cancel')}
              </ButtonWrapper>
              <ButtonWrapper
                data-testid='ResetCalendarConfirmationModal-ButtonWrapper-reset'
                color='secondary'
                onClick={handleSelectSubmit}
                disabled={!isCheckboxChecked}
              >
                {t('calendar.button.reset')}
              </ButtonWrapper>
            </ButtonsWrapper>
          </div>
        )}
      </Modal>
    </>
  )
}

export default ResetCalendarConfirmationModal

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 65px;
  justify-content: center;
`
const LoaderContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`
const ConfirmationSaltyImage = styled.img`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 112px;
  width: 145px;
`
const MainWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 716px;
  padding: 0 86px;
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 24px;
  width: 333px;
  margin-right: 75px;
`
const DescWrapper = styled.div`
  font-size: 18px;
  font-weight: bold;
  text-align: left;
  margin-bottom: 10px;
`
const ButtonWrapper = styled(Button)`
  width: 158px;
`
const ButtonContainer = styled(Button)`
  width: 158px;
`
const TitleIconContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 10px;
`

const TitleIconWrapper = styled.div`
  width: 68px;
  height: 68px;
  background-color: ${({ theme }) => theme.colors.main.primary500};
  border-radius: 50%;
`
const WarningCalendarIcon = styled.img``
const WarningCalendarWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`
const RadioButtonContainer = styled.div`
  margin-top: 10px;
`
